import type { AccountInfo, SessionUserInfo, SharedSessionData } from '../types';

import {
  ACCOUNT_QUERY,
  ACTIVE_ACCOUNT_SLOT_KEY,
  DATA_BROADCAST_CHANNEL_PREFIX,
  ESTABLISH_BROADCAST_CHANNEL_PREFIX,
  GLOBAL_STATE_CACHE_PREFIX,
  MULTITAB_LOCALSTORAGE_KEY_PREFIX,
  SESSION_ACCOUNT_PREFIX,
} from '../config';
import { getInitialLocationHash } from './routing';
const WORKER_NAME = typeof WorkerGlobalScope !== 'undefined' && globalThis.self instanceof WorkerGlobalScope
  ? globalThis.self.name : undefined;
const WORKER_ACCOUNT_SLOT = WORKER_NAME ? Number(new URLSearchParams(WORKER_NAME).get(ACCOUNT_QUERY)) : undefined;

export const ACCOUNT_SLOT = WORKER_ACCOUNT_SLOT || getAccountSlot(globalThis.location.href);

export const DATA_BROADCAST_CHANNEL_NAME = `${DATA_BROADCAST_CHANNEL_PREFIX}_${ACCOUNT_SLOT || 1}`;
export const ESTABLISH_BROADCAST_CHANNEL_NAME = `${ESTABLISH_BROADCAST_CHANNEL_PREFIX}_${ACCOUNT_SLOT || 1}`;
export const MULTITAB_STORAGE_KEY = `${MULTITAB_LOCALSTORAGE_KEY_PREFIX}_${ACCOUNT_SLOT || 1}`;
export const GLOBAL_STATE_CACHE_KEY = ACCOUNT_SLOT
  ? `${GLOBAL_STATE_CACHE_PREFIX}_${ACCOUNT_SLOT}` : GLOBAL_STATE_CACHE_PREFIX;

// Read once, at import time: the redirect that uses this runs while the app is booting, and by then a
// tab of this very session may already have claimed the slot for itself.
export const ACTIVE_ACCOUNT_SLOT = readActiveAccountSlot();

function readActiveAccountSlot() {
  // Absent in the API worker, which imports this module for `WORKER_ACCOUNT_SLOT`
  if (typeof localStorage === 'undefined') return undefined;

  const stored = Number(localStorage.getItem(ACTIVE_ACCOUNT_SLOT_KEY));
  if (!stored || Number.isNaN(stored)) return undefined;
  return stored;
}

export function storeActiveAccountSlot(slot: number) {
  localStorage.setItem(ACTIVE_ACCOUNT_SLOT_KEY, String(slot));
}

export function getAccountSlot(url: string) {
  const params = new URL(url).searchParams;
  const slot = params.get(ACCOUNT_QUERY);
  const slotNumber = slot ? Number(slot) : 1;
  if (!slotNumber || Number.isNaN(slotNumber) || slotNumber === 1) return undefined;
  return slotNumber;
}

// Every slot that actually holds account data, duplicates included. Choosing where a new account
// goes has to consult this rather than `getAccountsInfo`, which hides duplicate slots: a hidden slot
// still owns a session, so treating its number as free hands "Add account" a slot that is taken and
// signs the account living there out of its own place.
export function getOccupiedAccountSlots() {
  return new Set(readStoredSlots().filter((slot) => Boolean(getAccountInfo(slot))));
}

function readStoredSlots() {
  return Object.keys(localStorage)
    .filter((key) => key.startsWith(SESSION_ACCOUNT_PREFIX))
    .map((key) => Number(key.slice(SESSION_ACCOUNT_PREFIX.length)))
    .filter((slot) => slot > 0)
    .sort((a, b) => a - b);
}

// The slot that already holds this account, if any. Identity is the only reliable key here: phone
// numbers arrive in a different shape from every endpoint, so matching on them let the same account
// sign in twice.
export function findAccountSlotByUserId(userId: string) {
  return readStoredSlots().find((slot) => getAccountInfo(slot)?.userId === userId);
}

// A duplicate login navigates the tab to the slot that already owns the account, so the notice has to
// outlive that navigation. Session storage is per-tab, which is exactly the scope of this hand-off.
const DUPLICATE_SWITCH_KEY = 'tt-duplicate-account-switch';

export function markDuplicateAccountSwitch() {
  try {
    sessionStorage.setItem(DUPLICATE_SWITCH_KEY, '1');
  } catch {
    // A private window can refuse session storage; the redirect still has to happen.
  }
}

export function consumeDuplicateAccountSwitch() {
  try {
    if (!sessionStorage.getItem(DUPLICATE_SWITCH_KEY)) return false;
    sessionStorage.removeItem(DUPLICATE_SWITCH_KEY);
    return true;
  } catch {
    return false;
  }
}

// Drop the slots a second login left behind for an account that already had one. Keeping the
// lowest-numbered slot makes the choice identical in every tab, so two tabs can never evict each
// other, and the slot this tab runs on is never taken away from under it. Returns the slots that were
// dropped, so the caller can discard their sessions too — a slot whose account data is gone but whose
// session survives would still boot into that account the next time it is handed out.
export function pruneDuplicateAccountSlots() {
  const currentSlot = ACCOUNT_SLOT || 1;
  const keptSlotByUserId: Record<string, number> = {};
  const removedSlots: number[] = [];

  for (const slot of readStoredSlots()) {
    const userId = getAccountInfo(slot)?.userId;
    if (!userId) continue;

    if (keptSlotByUserId[userId] === undefined) {
      keptSlotByUserId[userId] = slot;
      continue;
    }

    if (slot === currentSlot) continue;

    localStorage.removeItem(`${SESSION_ACCOUNT_PREFIX}${slot}`);
    removedSlots.push(slot);
  }

  return removedSlots;
}

export function getAccountsInfo() {
  const allSlots = readStoredSlots();

  const currentSlot = ACCOUNT_SLOT || 1;
  const accountInfo: Record<number, AccountInfo> = {};
  const slotByUserId: Record<string, number> = {};

  for (const slot of allSlots) {
    const info = getAccountInfo(slot);
    if (!info) continue;

    // One account can occupy two slots: only the phone-number screen guards against a repeat login
    // (`AuthPhoneNumber`), so a QR or passkey login into a free slot silently duplicates an account
    // that is already signed in — showing it twice in the account switcher, each with its own stale
    // avatar snapshot. Collapse duplicates to one slot per user: the slot this tab runs on when it
    // is one of them, else the lowest-numbered.
    const keptSlot = slotByUserId[info.userId!];
    if (keptSlot !== undefined) {
      if (slot !== currentSlot) continue;
      delete accountInfo[keptSlot];
    }

    slotByUserId[info.userId!] = slot;
    accountInfo[slot] = info;
  }

  return accountInfo;
}

function getAccountInfo(slot: number): AccountInfo | undefined {
  const sessionData = loadSlotSession(slot);
  const {
    userId, avatarUri, color, emojiStatusId, firstName, lastName, isPremium, isTest, phone,
  } = sessionData || {};

  if (!userId) return undefined;

  return {
    userId,
    avatarUri,
    color,
    emojiStatusId,
    firstName,
    lastName,
    isPremium,
    isTest,
    phone,
  };
}

export function loadSlotSession(slot: number | undefined): SharedSessionData | undefined {
  try {
    const data = JSON.parse(localStorage.getItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`) || '{}') as SharedSessionData;
    if (!data.dcId && !data.userId) return undefined;
    return data;
  } catch (e) {
    return undefined;
  }
}

export function storeAccountData(slot: number | undefined, data: Partial<SessionUserInfo>) {
  const currentSlotData: SharedSessionData = loadSlotSession(slot) ?? {};

  const updatedSharedData: SharedSessionData = {
    ...currentSlotData,
    ...data,
  };

  if (!updatedSharedData.userId) return;

  writeSlotSession(slot, updatedSharedData);
}

export function writeSlotSession(slot: number | undefined, data: SharedSessionData) {
  localStorage.setItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`, JSON.stringify(data));
}

export function getAccountSlotUrl(slot: number, forLogin?: boolean, isTest?: boolean) {
  const url = new URL(globalThis.location.href);
  // A login link has to name its slot even when that slot is the first one. Without `?account=` the
  // URL is indistinguishable from an unaddressed launch, so the boot-time redirect in `App` sends it
  // to the account last in use — and "Add account" into a free first slot lands back on the current
  // account instead of the login screen
  if (slot !== 1 || forLogin) {
    url.searchParams.set(ACCOUNT_QUERY, String(slot));
  } else {
    url.searchParams.delete(ACCOUNT_QUERY);
  }

  if (isTest) {
    url.searchParams.set('test', 'true');
  } else {
    url.searchParams.delete('test');
  }

  url.hash = forLogin ? 'login' : '';
  return url.toString();
}

export function redirectToActiveAccount() {
  if (ACCOUNT_SLOT || !ACTIVE_ACCOUNT_SLOT || ACTIVE_ACCOUNT_SLOT === 1) return false;

  const url = new URL(globalThis.location.href);
  // An explicit `?account=1` is the user asking for the first slot — respect it
  if (url.searchParams.has(ACCOUNT_QUERY)) return false;

  const initialHash = getInitialLocationHash();
  // A `#login` hash means the user opened the "Add account" screen intentionally
  // (getAccountSlotUrl sets this hash when forLogin=true). Never redirect away from
  // the login flow — doing so drops the user into the already-active account and
  // makes the auth screen unreachable, producing the infinite-loop symptom.
  if (initialHash === 'login') return false;

  // The account may have been logged out since it was last used
  if (!loadSlotSession(ACTIVE_ACCOUNT_SLOT)) return false;

  url.searchParams.set(ACCOUNT_QUERY, String(ACTIVE_ACCOUNT_SLOT));
  url.hash = initialHash;
  globalThis.location.replace(url.toString());

  return true;
}

// Validate current version across all tabs to avoid conflicts
if (typeof window === 'object') {
  const versionChannel = new BroadcastChannel('tt-version');
  versionChannel.postMessage({ version: APP_VERSION });

  versionChannel.addEventListener('message', (event) => {
    const { version } = event.data;
    if (!version) return;
    if (semverCompare(APP_VERSION, version) === -1) {
      window.location.reload();
    }

    // If incoming version is older, send back the current version
    if (semverCompare(APP_VERSION, version) === 1) {
      versionChannel.postMessage({ version: APP_VERSION });
    }
  });
}

function semverCompare(a: string, b: string) {
  if (a.startsWith(`${b}-`)) return -1;
  if (b.startsWith(`${a}-`)) return 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'case', caseFirst: 'upper' });
}
